import * as d3 from 'd3'

import { ScaleId } from '../maps/types'
import { decodeQueryString as decode, encodeQueryString } from '../queryString'
import { GradientDefinition, bubbleGradients } from '../shared/gradients'
import {
  BubbleSelectOption,
  averageVolumes,
  colors,
  indexes,
  industries,
  marketCaps,
  sectors,
  sizes,
  xys,
} from './selects'
import { BubbleNode } from './types'

const DEFAULT_COLOR_SCALE = [
  '#ff4146',
  '#ea580c',
  '#ff9300',
  '#84cc16',
  '#34aa00',
  '#00d16d',
  '#14b8a6',
  '#18a3e0',
  '#5963d1',
  '#4f46e5',
  '#9425c2',
  '#c026d3',
  '#ff80ff',
  '#ff5888',
  '#ffe100',
]

function getValueWithFallback(values: BubbleSelectOption[], value: string | undefined, fallback: string) {
  const v = values.find((v) => v.value === value)

  return v?.value ?? fallback
}

function getDefaultValues(query: Record<string, string> = {}, extendedFilters: boolean = false) {
  let state: Record<string, string> = {
    x: getValueWithFallback(xys, query.x, 'sector'),
    y: getValueWithFallback(xys, query.y, 'lastChange'),
    size: getValueWithFallback(sizes, query.size, 'marketCap'),
    color: getValueWithFallback(colors, query.color, 'sector'),
    idx: getValueWithFallback(indexes, query.idx, 'sp500'),
    rangeX: query.rangeX ?? '',
    rangeY: query.rangeY ?? '',
  }

  if (extendedFilters) {
    state = {
      ...state,
      sec: getValueWithFallback(sectors, query.sec, ''),
      ind: getValueWithFallback(industries, query.ind, ''),
      cap: getValueWithFallback(marketCaps, query.cap, ''),
      sh_avgvol: getValueWithFallback(averageVolumes, query.sh_avgvol, ''),
      tickers: query.tickers ?? '',
      excludeTickers: query.excludeTickers ?? '',
    }
  }

  return state
}

function decodeQueryString(extendedFilters?: boolean) {
  return getDefaultValues(decode(), extendedFilters)
}

var percentile = function (arr: number[], excelPercentile: number) {
  var sequence = arr.concat().sort(function (a, b) {
    return a - b
  })
  var N = sequence.length
  var n = (N - 1) * excelPercentile + 1

  if (n === 1) {
    return sequence[0]
  } else if (n === N) {
    return sequence[N - 1]
  } else {
    var k = ~~n
    var d = n - k
    return sequence[k - 1] + d * (sequence[k] - sequence[k - 1])
  }
}

var getXScale = function (data: BubbleNode[], width: number) {
  var x = d3
    .scaleLinear()
    .range([0, width])
    .domain(d3.extent(data, (d: BubbleNode) => d.x) as [number, number])
    .nice(10)
  if (data.length === 1) {
    x.domain([x.domain()[0] * 0.9, x.domain()[0] * 1.1]).nice(10)
  }
  return x
}

var getYScale = function (data: BubbleNode[], height: number) {
  var y = d3
    .scaleLinear()
    .range([height, 0])
    .domain(d3.extent(data, (d: BubbleNode) => d.y) as [number, number])
    .nice(10)
  if (data.length === 1) {
    y.domain([y.domain()[0] * 0.9, y.domain()[0] * 1.1]).nice(10)
  }
  return y
}

var _colorScales: Record<string, any> = {}
function getColorScale(data: BubbleNode[], colorOption: string, colorScale = DEFAULT_COLOR_SCALE) {
  if (_colorScales[colorOption]) {
    return _colorScales[colorOption]
  }

  let c: any
  let colors: number[]
  let min: number
  let max: number
  let gradient: GradientDefinition
  let linearScale: d3.ScaleLinear<number, number, never>
  switch (colorOption) {
    case 'exchange':
    case 'sector':
    case 'industry':
    case 'country':
      c = d3.scaleOrdinal().range(colorScale)
      break
    case 'marketCap':
    case 'averageVolume':
    case 'relativeVolume':
    case 'lastVolume':
    case 'averageVolumeUsd':
    case 'lastVolumeUsd':
      colors = data.map((d) => Number(d.color)).filter(Number.isFinite)
      min = percentile(colors, 0.05)
      max = percentile(colors, 0.95)
      gradient = bubbleGradients['_relvol']
      linearScale = d3
        .scaleLinear()
        .domain([min, max])
        .range([0, gradient.colors.length - 1])
      c = (d?: number) => {
        if (typeof d === 'undefined' || d === null) {
          return gradient.nullColor
        }
        if (d > max) d = max
        if (d < min) d = min
        var i = Math.round(linearScale(d))
        return gradient.colors[i]
      }
      c.domain = () => [min, max]
      break
    case 'lastChange':
    case 'perf1w':
    case 'perf4w':
    case 'perf13w':
    case 'perf26w':
    case 'perf52w':
    case 'perfYtd':
      colors = data.map((d) => Number(d.color)).filter(Number.isFinite)

      min = percentile(colors, 0.05)
      max = percentile(colors, 0.95)
      max = Math.max(Math.abs(min), Math.abs(max))
      min = -max

      gradient = bubbleGradients[ScaleId.DayPerf]
      linearScale = d3
        .scaleLinear()
        .domain([min, max])
        .range([0, gradient.colors.length - 1])
      c = (d?: number) => {
        if (typeof d === 'undefined' || d === null) {
          return gradient.nullColor
        }
        if (d > max) d = max
        if (d < min) d = min
        var i = Math.round(linearScale(d))
        return gradient.colors[i]
      }
      c.domain = () => [min, max]
      break
    case 'consRecom':
      min = 5
      max = 1
      gradient = bubbleGradients[ScaleId.DayPerf]
      linearScale = d3
        .scaleLinear()
        .domain([min, max])
        .range([0, gradient.colors.length - 1])
      c = (d?: number) => {
        if (typeof d === 'undefined' || d === null) {
          return gradient.nullColor
        }
        if (d < max) d = max
        if (d > min) d = min
        var i = Math.round(linearScale(d))
        return gradient.colors[i]
      }
      c.domain = () => [min, max]
      break
    case 'const':
    default:
      c = function () {
        return '#18a3e0'
      }
      c.domain = () => ['Constant']
      break
  }

  if (data.length > 0) {
    _colorScales[colorOption] = c
  }
  return c
}

var getSizeScale = function (data: BubbleNode[]) {
  var r = d3
    .scaleSqrt()
    .range([0, 40])
    .domain(d3.extent(data, (d: BubbleNode) => d.size) as [number, number])
    .nice(10)

  if (r.domain()[0] === 1 && r.domain()[1] === 1) {
    r.domain([0, 50])
  }
  return r
}

var getScales = function (
  data: BubbleNode[],
  width: number,
  height: number,
  colorOption: string,
  colorScale?: string[]
) {
  return {
    x: getXScale(data, width),
    y: getYScale(data, height),
    r: getSizeScale(data),
    c: getColorScale(data, colorOption, colorScale),
  }
}

function generatePresetUrl(presetConfig: Record<string, any>, baseQuery?: URLSearchParams) {
  const query = encodeQueryString({ ...Object.fromEntries(baseQuery ?? []), ...presetConfig })

  return `${window.location.pathname}?${query}`
}

function formatValue(value: number | string, format?: string, finiteFormatting?: boolean) {
  if (finiteFormatting && !Number.isFinite(value)) {
    return '—'
  }

  if (typeof value === 'number') {
    const absValue = Math.abs(value)

    if (absValue < 1000) {
      value = value.toFixed(2)
    } else if (absValue < 1000 * 1000) {
      value = (value / 1000).toFixed(2) + 'K'
    } else if (absValue < 1000 * 1000 * 1000) {
      value = (value / 1000000).toFixed(2) + 'M'
    } else {
      value = (value / 1000000000).toFixed(2) + 'B'
    }
  }

  if (format === '%') {
    value += '%'
  }

  return value
}

export {
  getDefaultValues,
  encodeQueryString,
  decodeQueryString,
  percentile,
  getXScale,
  getYScale,
  getColorScale,
  getSizeScale,
  getScales,
  generatePresetUrl,
  formatValue,
}
